import { useState, useRef, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { 
  User, Globe, Linkedin, Phone, Shield, Eye, EyeOff,
  Award, FolderOpen, Edit3, Save, X, Flame, CreditCard,
  Users, BarChart3, Camera, CalendarIcon, Lightbulb
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects } from "@/hooks/useParticipantData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import GamificationSection from "@/components/profile/GamificationSection";
import PaymentsSection from "@/components/profile/PaymentsSection";
import ReferralsSection from "@/components/profile/ReferralsSection";
import CertificationsWidget from "@/components/profile/CertificationsWidget";
import MetricsSection from "@/components/profile/MetricsSection";
import SuggestionsSection from "@/components/profile/SuggestionsSection";
import PasswordChangeCard from "@/components/profile/PasswordChangeCard";

const ProfilePage = () => {
  const { profile, user } = useAuth();
  const { data: projects = [] } = useProjects();

  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("perfil");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam) {
      setActiveTab(tabParam);
    }
    if (params.get("license") === "success") {
      toast.success("¡Licencia Pro activada exitosamente! Disfruta de todos los beneficios.");
      setActiveTab("pagos");
      params.delete("license");
      const newUrl = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasOrg = !!profile?.organization_id;

  // Form state initialized from profile
  const [formData, setFormData] = useState({
    full_name: profile?.full_name ?? "",
    position: profile?.position ?? "",
    area: profile?.area ?? "",
    phone: profile?.phone ?? "",
    linkedin_url: profile?.linkedin_url ?? "",
    bio: profile?.bio ?? "",
    birth_date: profile?.birth_date ? new Date(profile.birth_date) : undefined as Date | undefined,
    company: profile?.company ?? "",
    sector: profile?.sector ?? "",
  });

  // Fetch organization name from DB
  const { data: orgData } = useQuery({
    queryKey: ['user-organization', profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return null;
      const { data } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', profile.organization_id)
        .single();
      return data;
    },
    enabled: !!profile?.organization_id,
  });

  // Fetch business areas for area selector
  const { data: businessAreas } = useQuery({
    queryKey: ['business_areas'],
    queryFn: async () => {
      const { data, error } = await supabase.from('business_areas').select('id, name').order('name');
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  // Privacy toggles
  const [isPublic, setIsPublic] = useState<boolean>(profile?.is_public ?? true);
  const [showProjects, setShowProjects] = useState<boolean>(profile?.show_projects ?? true);
  const [waOptIn, setWaOptIn] = useState<boolean>((profile as any)?.whatsapp_opt_in ?? false);

  // Sync form when profile changes (initial load)
  const [lastProfileId, setLastProfileId] = useState<string | null>(null);
  if (profile && profile.id !== lastProfileId) {
    setLastProfileId(profile.id);
    setFormData({
      full_name: profile.full_name ?? "",
      position: profile.position ?? "",
      area: profile.area ?? "",
      phone: profile.phone ?? "",
      linkedin_url: profile.linkedin_url ?? "",
      bio: profile.bio ?? "",
      birth_date: profile.birth_date ? new Date(profile.birth_date) : undefined,
      company: profile.company ?? "",
      sector: profile.sector ?? "",
    });
    setIsPublic(profile.is_public ?? true);
    setShowProjects(profile.show_projects ?? true);
    setWaOptIn((profile as any).whatsapp_opt_in ?? false);
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!user) return;
    setIsSaving(true);
    try {
      const updateData: Record<string, any> = {
        full_name: formData.full_name,
        position: formData.position,
        area: formData.area || null,
        phone: formData.phone,
        linkedin_url: formData.linkedin_url,
        bio: formData.bio,
        birth_date: formData.birth_date ? format(formData.birth_date, 'yyyy-MM-dd') : null,
      };
      if (!hasOrg) {
        updateData.company = formData.company || null;
        updateData.sector = formData.sector || null;
      }
      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);
      if (error) throw error;
      toast.success("Perfil actualizado correctamente");
      setIsEditing(false);
    } catch (err: any) {
      toast.error("Error al guardar", { description: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrivacyToggle = async (field: 'is_public' | 'show_projects', value: boolean) => {
    if (!user) return;
    if (field === 'is_public') setIsPublic(value);
    else setShowProjects(value);

    const { error } = await supabase
      .from('profiles')
      .update({ [field]: value })
      .eq('id', user.id);
    if (error) {
      toast.error("Error al actualizar privacidad");
      // Revert
      if (field === 'is_public') setIsPublic(!value);
      else setShowProjects(!value);
    }
  };

  const handleWhatsAppToggle = async (value: boolean) => {
    if (!user) return;
    setWaOptIn(value);
    const nowIso = new Date().toISOString();
    const { error } = await supabase
      .from('profiles')
      .update(
        value
          ? { whatsapp_opt_in: true, whatsapp_opt_in_at: nowIso, whatsapp_opt_in_source: 'profile_settings', whatsapp_opt_out_at: null }
          : { whatsapp_opt_in: false, whatsapp_opt_out_at: nowIso },
      )
      .eq('id', user.id);
    if (error) {
      toast.error("No se pudo actualizar las notificaciones de WhatsApp");
      setWaOptIn(!value); // revert
    } else {
      toast.success(value ? "Notificaciones de WhatsApp activadas" : "Notificaciones de WhatsApp desactivadas");
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Solo se permiten imágenes");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen no debe superar 2MB");
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${user.id}/avatar.${ext}`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      const avatar_url = `${urlData.publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url })
        .eq('id', user.id);
      if (updateError) throw updateError;

      toast.success("Avatar actualizado");
      // Force reload profile
      window.location.reload();
    } catch (err: any) {
      toast.error("Error al subir avatar", { description: err.message });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Completado';
      case 'in_progress': return 'En progreso';
      case 'ideation': return 'Ideación';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'in_progress': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)
    : '??';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Mi Perfil</h1>
          <p className="text-muted-foreground">
            Gestiona tu información, gamificación, pagos y referidos
          </p>
        </div>
        {activeTab === "perfil" && (
          <Button 
            variant={isEditing ? "outline" : "default"}
            className="gap-2"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? (
              <><X className="w-4 h-4" /> Cancelar</>
            ) : (
              <><Edit3 className="w-4 h-4" /> Editar Perfil</>
            )}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-flex">
          <TabsTrigger value="perfil" className="gap-2">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Perfil</span>
          </TabsTrigger>
          <TabsTrigger value="metricas" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Métricas</span>
          </TabsTrigger>
          <TabsTrigger value="gamificacion" className="gap-2">
            <Flame className="w-4 h-4" />
            <span className="hidden sm:inline">Mi Racha</span>
          </TabsTrigger>
          <TabsTrigger value="pagos" className="gap-2">
            <CreditCard className="w-4 h-4" />
            <span className="hidden sm:inline">Pagos</span>
          </TabsTrigger>
          <TabsTrigger value="referidos" className="gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Referidos</span>
          </TabsTrigger>
          <TabsTrigger value="sugerencias" className="gap-2">
            <Lightbulb className="w-4 h-4" />
            <span className="hidden sm:inline">Sugerencias</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="perfil" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Profile Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Información Personal
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex flex-col items-center sm:flex-row sm:items-start gap-6">
                    <div className="relative group shrink-0">
                      <Avatar className="w-24 h-24 border-2 border-primary/20">
                        <AvatarImage src={profile?.avatar_url ?? undefined} />
                        <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      {isEditing && (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploadingAvatar}
                          className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        >
                          <Camera className="w-6 h-6 text-white" />
                        </button>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleAvatarUpload}
                      />
                    </div>
                    <div className="flex-1 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Nombre completo</Label>
                          <Input 
                            id="name" 
                            value={formData.full_name}
                            onChange={(e) => handleInputChange('full_name', e.target.value)}
                            disabled={!isEditing}
                            className="bg-background/50"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Correo electrónico</Label>
                          <Input 
                            id="email" 
                            value={profile?.email ?? ""}
                            disabled
                            className="bg-background/50"
                          />
                        </div>
                      </div>
                      {/* Birth Date */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Fecha de nacimiento</Label>
                          {isEditing ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    "w-full justify-start text-left font-normal bg-background/50",
                                    !formData.birth_date && "text-muted-foreground"
                                  )}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {formData.birth_date
                                    ? format(formData.birth_date, "PPP", { locale: es })
                                    : "Seleccionar fecha"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={formData.birth_date}
                                  onSelect={(date) => setFormData(prev => ({ ...prev, birth_date: date }))}
                                  disabled={(date) => date > new Date() || date < new Date("1930-01-01")}
                                  initialFocus
                                  className={cn("p-3 pointer-events-auto")}
                                  captionLayout="dropdown-buttons"
                                  fromYear={1930}
                                  toYear={new Date().getFullYear()}
                                  locale={es}
                                  defaultMonth={formData.birth_date ?? new Date(1990, 0, 1)}
                                />
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <Input
                              value={formData.birth_date ? format(formData.birth_date, "PPP", { locale: es }) : "—"}
                              disabled
                              className="bg-background/50"
                            />
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="company">Empresa</Label>
                          {hasOrg ? (
                            <Input 
                              id="company" 
                              value={orgData?.name ?? "Cargando..."}
                              disabled
                              className="bg-background/50"
                            />
                          ) : (
                            <Input 
                              id="company" 
                              placeholder="Nombre de tu empresa"
                              value={formData.company}
                              onChange={(e) => handleInputChange('company', e.target.value)}
                              disabled={!isEditing}
                              className="bg-background/50"
                            />
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="position">Cargo</Label>
                          <Input 
                            id="position" 
                            value={formData.position}
                            onChange={(e) => handleInputChange('position', e.target.value)}
                            disabled={!isEditing}
                            className="bg-background/50"
                          />
                        </div>
                        {!hasOrg && (
                          <div className="space-y-2">
                            <Label htmlFor="sector">Sector</Label>
                            <Input 
                              id="sector" 
                              placeholder="Ej: Tecnología, Finanzas..."
                              value={formData.sector}
                              onChange={(e) => handleInputChange('sector', e.target.value)}
                              disabled={!isEditing}
                              className="bg-background/50"
                            />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label htmlFor="area">Área (opcional)</Label>
                          {isEditing ? (
                            <SearchableCombobox
                              value={formData.area || "__none__"}
                              onSelect={(v) => handleInputChange('area', v === "__none__" ? "" : v)}
                              placeholder="Seleccionar área"
                              searchPlaceholder="Buscar área..."
                              triggerClassName="bg-background/50"
                              options={[
                                { value: "__none__", label: "Sin área", keywords: ['ninguna','none'] },
                                ...(businessAreas ?? []).map((a) => ({ value: a.name, label: a.name })),
                              ]}
                            />
                          ) : (
                            <Input 
                              value={formData.area || "—"}
                              disabled
                              className="bg-background/50"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="linkedin">LinkedIn</Label>
                      <div className="relative">
                        <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input 
                          id="linkedin" 
                          placeholder="linkedin.com/in/usuario"
                          value={formData.linkedin_url}
                          onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                          disabled={!isEditing}
                          className="pl-10 bg-background/50"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Teléfono</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input 
                          id="phone" 
                          placeholder="+52 123 456 7890"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          disabled={!isEditing}
                          className="pl-10 bg-background/50"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">Biografía</Label>
                    <Textarea 
                      id="bio" 
                      placeholder="Cuéntanos sobre ti, tu experiencia y objetivos..."
                      value={formData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      disabled={!isEditing}
                      className="bg-background/50 min-h-[100px]"
                    />
                  </div>

                  {isEditing && (
                    <div className="flex justify-end">
                      <Button className="gap-2" onClick={handleSave} disabled={isSaving}>
                        <Save className="w-4 h-4" />
                        {isSaving ? "Guardando..." : "Guardar Cambios"}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Certifications Widget */}
              <CertificationsWidget />

              {/* Projects Card */}
              {showProjects && (
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FolderOpen className="w-5 h-5 text-primary" />
                      Mis Proyectos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {projects.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No tienes proyectos aún</p>
                    ) : (
                      <div className="space-y-3">
                        {projects.map((project) => (
                          <div 
                            key={project.id}
                            className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-border/50"
                          >
                            <div>
                              <p className="font-medium text-foreground">{project.name}</p>
                              <p className="text-sm text-muted-foreground">
                                Solución Tipo {project.solution_type ?? '—'}
                              </p>
                            </div>
                            <Badge 
                              variant="outline"
                              className={getStatusColor(project.status ?? 'ideation')}
                            >
                              {getStatusLabel(project.status ?? 'ideation')}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Privacy Settings Card */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Privacidad
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {isPublic ? (
                          <Eye className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-muted-foreground" />
                        )}
                        <Label htmlFor="public-profile" className="font-medium">
                          Perfil Público
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Permite que otros vean tu perfil
                      </p>
                    </div>
                    <Switch 
                      id="public-profile"
                      checked={isPublic}
                      onCheckedChange={(val) => handlePrivacyToggle('is_public', val)}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="w-4 h-4 text-muted-foreground" />
                        <Label htmlFor="show-projects" className="font-medium">
                          Mostrar Proyectos
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Muestra tus proyectos en tu perfil
                      </p>
                    </div>
                    <Switch
                      id="show-projects"
                      checked={showProjects}
                      onCheckedChange={(val) => handlePrivacyToggle('show_projects', val)}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-emerald-400" />
                        <Label htmlFor="wa-optin" className="font-medium">
                          Notificaciones por WhatsApp
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Recordatorios de sesiones, entregables y avisos del programa. Podés desactivarlo cuando quieras.
                      </p>
                    </div>
                    <Switch
                      id="wa-optin"
                      checked={waOptIn}
                      onCheckedChange={handleWhatsAppToggle}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Password Change Card */}
              <PasswordChangeCard />

              {/* Stats Card */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Estadísticas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Proyectos</span>
                    <span className="font-bold text-foreground">{projects.length}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Profile Visibility Preview */}
              <Card className="glass-card border-primary/20">
                <CardContent className="pt-6">
                  <div className="text-center space-y-2">
                    <Globe className="w-8 h-8 mx-auto text-primary/60" />
                    <p className="text-sm text-muted-foreground">
                      {isPublic 
                        ? "Tu perfil es visible para otros participantes"
                        : "Tu perfil es privado"
                      }
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="metricas" className="mt-6">
          <MetricsSection />
        </TabsContent>

        <TabsContent value="gamificacion" className="mt-6">
          <GamificationSection />
        </TabsContent>

        <TabsContent value="pagos" className="mt-6">
          <PaymentsSection />
        </TabsContent>

        <TabsContent value="referidos" className="mt-6">
          <ReferralsSection />
        </TabsContent>

        <TabsContent value="sugerencias" className="mt-6">
          <SuggestionsSection />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfilePage;
